import React from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import decode from "jwt-decode";
import HistoryIcon from "@mui/icons-material/History";

import { useStateAuthValue } from "../context/AuthState";
import Colors from "../theme/Colors";
import {
  getSectionByPath,
  FEATURED_PATHS_BY_ROLE,
} from "../constants/sections";
import { getRecentSectionPaths } from "../utils/recentSections";

const getUserName = (token) => {
  try {
    const decoded = decode(token);
    return decoded?.sub || decoded?.user_name || null;
  } catch {
    return null;
  }
};

// Tarjeta elevable (design.md §2.1) reutilizada por ambos grids.
const SectionCard = ({ section, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      background: "#ffffff",
      border: `1px solid ${Colors.GrisBorde}`,
      borderRadius: "14px",
      padding: "1.5rem",
      display: "flex",
      alignItems: "center",
      gap: "1rem",
      cursor: "pointer",
      transition: "0.2s ease-in-out all",
      boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: "0 12px 24px rgba(0,0,0,0.10)",
        borderColor: Colors.AzulMediline,
      },
    }}
  >
    {/* Icono en chip de fondo suave (design.md §2.4), variante azul (primario) */}
    <Box
      sx={{
        background: Colors.AzulMediline10,
        color: Colors.AzulMediline,
        width: "52px",
        height: "52px",
        borderRadius: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        "& svg": { fontSize: "28px" },
      }}
    >
      {section.icon}
    </Box>
    {/* Distribución título + subtítulo (design.md §2.2) */}
    <Box>
      <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: Colors.AzulMediline }}>
        {section.title}
      </Typography>
      <Typography sx={{ fontSize: "0.875rem", color: Colors.GrisMedio }}>
        {section.description}
      </Typography>
    </Box>
  </Box>
);

const SectionGrid = ({ title, icon, sections, onNavigate }) => (
  <Box sx={{ marginBottom: "2.5rem" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1rem" }}>
      {icon}
      <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: Colors.AzulMediline }}>
        {title}
      </Typography>
    </Box>
    {/* Grilla responsive de tarjetas (design.md §2.5) */}
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
        gap: "1.5rem",
      }}
    >
      {sections.map((section) => (
        <SectionCard
          key={section.path}
          section={section}
          onClick={() => onNavigate(section.path)}
        />
      ))}
    </Box>
  </Box>
);

const Home = () => {
  const [{ userRole, userToken }] = useStateAuthValue();
  const navigate = useNavigate();

  const userName = getUserName(userToken);

  // Accesos recientes: paths guardados en localStorage, resueltos contra el
  // catálogo y filtrados por el rol actual (descarta secciones inaccesibles).
  const recentSections = getRecentSectionPaths()
    .map((path) => getSectionByPath(path, userRole))
    .filter(Boolean);

  // Grid estático curado por rol (fallback / acceso rápido siempre visible).
  const featuredSections = (FEATURED_PATHS_BY_ROLE[userRole] || [])
    .map((path) => getSectionByPath(path, userRole))
    .filter(Boolean);

  return (
    <Box sx={{ padding: { xs: "1.5rem", md: "3rem" } }}>
      {/* Cabecera de bienvenida — patrón "header banner con gradiente" (design.md §2.3), variante azul (primario) */}
      <Box
        sx={{
          background: `linear-gradient(195deg, ${Colors.AzulMediline}, ${Colors.AzulMedilineClaro})`,
          borderRadius: "15px",
          color: "white",
          padding: { xs: "1.5rem", md: "2rem 2.5rem" },
          marginBottom: "2.5rem",
          boxShadow: "0 8px 24px rgba(0, 44, 84, 0.25)",
        }}
      >
        <Typography sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 700, letterSpacing: "0.5px" }}>
          Hola{userName ? ` ${userName}` : ""} 👋
        </Typography>
        <Typography sx={{ fontSize: "1rem", opacity: 0.9, marginTop: "0.25rem" }}>
          Bienvenido al Backoffice de Mediline
        </Typography>
      </Box>

      {recentSections.length > 0 && (
        <SectionGrid
          title="Accesos recientes"
          icon={<HistoryIcon sx={{ color: Colors.AzulMediline }} />}
          sections={recentSections}
          onNavigate={navigate}
        />
      )}

      {featuredSections.length > 0 && (
        <SectionGrid
          title="Accesos rápidos"
          sections={featuredSections}
          onNavigate={navigate}
        />
      )}
    </Box>
  );
};

export default Home;
